import { z } from 'zod';

import { UnitSystem } from '@optical/utils';

import { PlayerStatisticsSummaryMetric } from '../home';
import { Locale } from './locale';

export type UserSettings = z.output<typeof userSettingsSchema>;
export type UserSettingsDTO = z.input<typeof userSettingsSchema>;

/**
 * Returns the default user settings. Used when there is no owning user to read settings for, e.g.
 * when rendering a report that was created by an internal task rather than a user.
 */
export const getDefaultUserSettings = (): UserSettings => userSettingsSchema.parse({});

export const userSettingsSchema = z.object({
  allowUsageAnalytics: z.boolean().catch(true),
  locale: z.enum(Locale).catch(Locale.EN_US),
  notifyAssignmentsReady: z.boolean().catch(true),
  selectedTeamId: z.uuid().nullable().catch(null),
  unitSystem: z.enum(UnitSystem).catch(UnitSystem.METRIC),

  explore: z
    .object({
      selectedPresetId: z.uuid().nullable().catch(null),
    })
    .catch({
      selectedPresetId: null,
    }),
  home: z
    .object({
      playerOverview: z.object({
        selectedMetric: z
          .enum(PlayerStatisticsSummaryMetric)
          .catch(PlayerStatisticsSummaryMetric.FGA),
      }),
      statisticsSummary: z
        .object({
          /** The group the summary is scoped to; `null` means the whole team. */
          selectedGroupId: z.uuid().nullable().catch(null),
        })
        .catch({
          selectedGroupId: null,
        }),
    })
    .catch({
      playerOverview: {
        selectedMetric: PlayerStatisticsSummaryMetric.FGA,
      },
      statisticsSummary: {
        selectedGroupId: null,
      },
    }),
  playerReport: z
    .object({
      performanceTrends: z.object({
        expanded: z.boolean().catch(true),
      }),
      playerProfile: z.object({
        expanded: z.boolean().catch(true),
      }),
      shotAreas: z.object({
        expanded: z.boolean().catch(true),
      }),
      shotLoad: z.object({
        expanded: z.boolean().catch(true),
      }),
      shotTrends: z.object({
        expanded: z.boolean().catch(true),
        selectedMetric: z.enum(['fgp', 'twopp', 'threepp', 'efgp', 'ftp']).catch('fgp'),
      }),
      scope: z.enum(['1M', '3M', '6M']).catch('1M'),
    })
    .catch({
      performanceTrends: {
        expanded: true,
      },
      playerProfile: {
        expanded: true,
      },
      shotAreas: {
        expanded: true,
      },
      shotLoad: {
        expanded: true,
      },
      shotTrends: {
        expanded: true,
        selectedMetric: 'fgp',
      },
      scope: '1M',
    }),
  roster: z
    .object({
      displayMode: z.enum(['list', 'grid']).catch('grid'),
    })
    .catch({
      displayMode: 'grid',
    }),
  sessionReport: z
    .object({
      performanceStats: z.object({
        expanded: z.boolean().catch(true),
      }),
      shotAreas: z.object({
        expanded: z.boolean().catch(true),
      }),
      shotLoad: z.object({
        expanded: z.boolean().catch(true),
      }),
      shotMap: z.object({
        expanded: z.boolean().catch(true),
      }),
      shotStats: z.object({
        expanded: z.boolean().catch(true),
      }),
    })
    .catch({
      performanceStats: {
        expanded: true,
      },
      shotAreas: {
        expanded: true,
      },
      shotLoad: {
        expanded: true,
      },
      shotMap: {
        expanded: true,
      },
      shotStats: {
        expanded: true,
      },
    }),
  weeklyReport: z
    .object({
      shotAreas: z.object({
        expanded: z.boolean().catch(true),
        selectedAreaMap: z.enum(['fgp', 'distr', 'xvalue']).catch('fgp'),
      }),
      shotStats: z.object({
        expanded: z.boolean().catch(true),
      }),
    })
    .catch({
      shotAreas: {
        expanded: true,
        selectedAreaMap: 'fgp',
      },
      shotStats: {
        expanded: true,
      },
    }),
});
